/**
 * PlateDetectionResults
 *
 * Design: License plate focused with OCR-style display
 * - Prominent plate number badges
 * - Ranked list for top plates
 * - Grid view for all unique plates
 */
import React from "react";
import { PlateDetectionResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import { Car, Hash, Award, List, Clock, Film, Eye } from "lucide-react";

interface PlateDetectionResultsProps {
  results: ResultsType;
}

export const PlateDetectionResults: React.FC<PlateDetectionResultsProps> = ({
  results,
}) => {
  const isVideo = results.type === "video";
  const apiResults = results as any;
  const totalPlates = apiResults.total_plate_detections || 0;
  const uniquePlatesCount = apiResults.unique_plates_count || 0;
  const uniquePlatesData = apiResults.unique_plates || {};
  const topPlates = apiResults.top_plates || [];
  const videoDuration = apiResults.video_duration_seconds || 0;
  const framesProcessed = apiResults.frames_processed || 0;
  const videoFps = apiResults.video_fps || 0;

  const uniquePlatesArray = Object.entries(uniquePlatesData)
    .map(([plate, count]) => ({ plate_number: plate, count: count as number }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Detections */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500/20 via-slate-800/50 to-slate-900 border border-primary-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary-400" />
              </div>
              <span className="text-slate-400 text-sm">Total Detections</span>
            </div>
            <p className="text-4xl font-bold text-white">
              {formatNumber(totalPlates)}
            </p>
          </div>
        </div>

        {/* Unique Plates */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500/20 via-slate-800/50 to-slate-900 border border-violet-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Hash className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-slate-400 text-sm">Unique Plates</span>
            </div>
            <p className="text-4xl font-bold text-white">
              {formatNumber(uniquePlatesCount)}
            </p>
          </div>
        </div>

        {/* Video Duration / Detection Rate */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/20 via-slate-800/50 to-slate-900 border border-emerald-500/30 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                {isVideo ? (
                  <Film className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Car className="w-5 h-5 text-emerald-400" />
                )}
              </div>
              <span className="text-slate-400 text-sm">
                {isVideo ? "Video Duration" : "Detection Rate"}
              </span>
            </div>
            <p className="text-4xl font-bold text-white">
              {isVideo
                ? `${videoDuration.toFixed(1)}s`
                : `${totalPlates > 0 ? 100 : 0}%`}
            </p>
            {isVideo && (
              <p className="text-slate-500 text-xs mt-1">
                {formatNumber(framesProcessed)} frames @ {videoFps.toFixed(0)}{" "}
                FPS
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Top Detected Plates - Podium Style */}
      {topPlates.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Award className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Top Detected Plates</h3>
              <p className="text-slate-400 text-sm">
                Most frequently seen license plates
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {topPlates
              .slice(0, 5)
              .map(([plate, count]: [string, number], idx: number) => (
                <div
                  key={idx}
                  className="flex items-center gap-4 bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 hover:border-primary-500/30 transition-colors"
                >
                  {/* Rank Badge */}
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                      idx === 0
                        ? "bg-amber-500/20 text-amber-400"
                        : idx === 1
                        ? "bg-slate-400/20 text-slate-300"
                        : idx === 2
                        ? "bg-orange-600/20 text-orange-400"
                        : "bg-slate-700/50 text-slate-500"
                    }`}
                  >
                    {idx + 1}
                  </div>

                  {/* Plate Number - License Plate Style */}
                  <div className="flex-1">
                    <div className="inline-block bg-gradient-to-r from-slate-700 to-slate-800 border-2 border-slate-600 rounded-lg px-4 py-2">
                      <span className="text-white font-mono font-bold text-lg tracking-wider">
                        {plate}
                      </span>
                    </div>
                  </div>

                  {/* Count */}
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary-400">
                      {count}
                    </p>
                    <p className="text-slate-500 text-xs">detections</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* All Unique Plates Grid */}
      {uniquePlatesArray.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <List className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">All Unique Plates</h3>
                <p className="text-slate-400 text-sm">
                  {uniquePlatesArray.length} plates detected
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {uniquePlatesArray.map((plate, idx) => (
              <div
                key={idx}
                className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50 flex items-center justify-between hover:border-primary-500/30 transition-colors"
              >
                <span className="text-primary-400 font-mono font-semibold text-sm truncate">
                  {plate.plate_number}
                </span>
                <span className="text-slate-400 text-xs bg-slate-800 px-2 py-1 rounded-full ml-2 flex-shrink-0">
                  ×{plate.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
